/**
 * Header household picker — the UCommandPalette-backed selector that
 * lets Admin / Advisor / Representative users switch the active
 * household for the rest of the layout.
 *
 * Extracted from layouts/default.vue. Owns:
 *   - Search-term ref + 250ms debounce (avoids one fetch per keystroke).
 *   - `/api/ui/households` fetch with role-gated `immediate`/`watch`
 *     (Clients shouldn't hit the endpoint at all — it 403s for them).
 *   - The "groups" projection that feeds UCommandPalette.
 *   - The onSelect handler that persists the chosen household into the
 *     tab session store and navigates to /household/overview.
 */
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useTabSessionStore } from '~/stores/tabSession'
import type { Household } from '~/types'

const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})

export function useHouseholdPicker() {
  const tabSession = useTabSessionStore()
  const router = useRouter()
  const { hasRole } = useAuth()
  const { hashId } = useMasking()

  const canViewSelector =
    hasRole('Admin.Read') || hasRole('Admin.Write')
    || hasRole('Advisor') || hasRole('Representative')

  const searchTerm = ref('')
  const selectedHousehold = ref<any>(null)

  // --- Header picker filters: AUM range + inception (start-date) range. ---
  // Sent to the server so they filter the WHOLE household set, not just the
  // currently-loaded page. Raw refs drive the inputs; debounced copies feed
  // the fetch so typing an amount doesn't fire a request per keystroke.
  // startDate is ISO 'yyyy-MM-dd', so inception bounds are plain ISO strings.
  const minValue = ref('')
  const maxValue = ref('')
  const inceptionFrom = ref('')
  const inceptionTo = ref('')
  const minValueD = refDebounced(minValue, 400)
  const maxValueD = refDebounced(maxValue, 400)
  const inceptionFromD = refDebounced(inceptionFrom, 400)
  const inceptionToD = refDebounced(inceptionTo, 400)

  // Sort — backend ApplySort tokens. Independent of the filters (it re-orders
  // the whole list rather than subsetting it), so it's NOT part of
  // pickerFilterActive and Reset leaves it alone. Default mirrors the backend.
  const sortBy = ref('-currentValue')
  const sortOptions = [
    { label: 'AUM (high → low)', value: '-currentValue' },
    { label: 'AUM (low → high)', value: 'currentValue' },
    { label: 'Inception (newest first)', value: '-startDate' },
    { label: 'Inception (oldest first)', value: 'startDate' }
  ]

  // --- "Advisor" filter: pick a representative; households are then filtered
  // to that rep's book. Admin + Advisor ONLY (a Representative only ever sees
  // their own households, so it's hidden in the UI + the endpoint 403s for
  // them). The rep list is its own search-driven fetch. ---
  const canFilterByRep = hasRole('Admin.Read') || hasRole('Admin.Write') || hasRole('Advisor')
  const repSearch = ref('')
  const repSearchD = refDebounced(repSearch, 250)
  const repId = ref<number | null>(null)
  const selectedRepName = ref('')

  const repQuery = computed(() => ({
    search: repSearchD.value.length >= 2 ? repSearchD.value : undefined,
    page: 1,
    pageSize: repSearchD.value ? 200 : 25,
    isActive: 'all'
  }))
  const { data: repsResp, status: repsStatus } = useFetch('/api/ui/representatives', {
    query: repQuery,
    watch: canFilterByRep ? [repSearchD] : false,
    immediate: canFilterByRep,
    credentials: 'include',
    default: () => ({ items: [] }),
    key: computed(() => `picker-reps:${repSearchD.value}`)
  })
  const repItems = computed(() =>
    (repsResp.value?.items ?? []).map((r: any) => ({
      repId: r.id as number,
      label: (r.fullName || r.repNo || `#${r.id}`) as string,
      sub: [r.repNo, r.company].filter(Boolean).join(' • ')
    }))
  )
  const repIsSearching = computed(() => repsStatus.value === 'pending')
  function pickRep(rep: { repId: number, label: string }) {
    repId.value = rep.repId
    selectedRepName.value = rep.label
  }
  function clearRep() {
    repId.value = null
    selectedRepName.value = ''
    repSearch.value = ''
  }

  // Button lights up immediately off the raw refs (no debounce wait).
  const pickerFilterActive = computed(() =>
    !!minValue.value || !!maxValue.value || !!inceptionFrom.value || !!inceptionTo.value
    || repId.value != null
  )
  function resetPickerFilters() {
    minValue.value = ''
    maxValue.value = ''
    inceptionFrom.value = ''
    inceptionTo.value = ''
    clearRep()
  }

  // 250ms debounce so the typed search doesn't fire one fetch per
  // keystroke. The watch lives at composable scope; Nuxt cleans it up
  // when the layout unmounts.
  //
  // `debouncePending` flips ON the moment the user types and OFF the
  // moment the debounce timer fires. That state feeds the picker's
  // `isSearching` flag below so the loading spinner only shows when
  // there's actual work in flight — either a pending debounce or a
  // pending fetch — and not "forever as long as the input has text"
  // (the previous heuristic).
  const debounced = ref('')
  const debouncePending = ref(false)
  let t: any
  watch(searchTerm, (v) => {
    clearTimeout(t)
    debouncePending.value = true
    t = setTimeout(() => {
      debounced.value = v
      debouncePending.value = false
    }, 250)
  })

  const term = computed(() => debounced.value)

  const filterActiveForFetch = computed(() =>
    !!minValueD.value || !!maxValueD.value || !!inceptionFromD.value || !!inceptionToD.value
    || repId.value != null
  )

  const pageSizeHousehold = computed(() => {
    // A filter (even without a search term) should surface as many matches
    // as the picker can hold, not just the default first page.
    if (filterActiveForFetch.value) return 200
    const value = debounced.value
    if (!value) return 25
    if (value.length === 1) return 50
    return 200
  })

  // When previewing AS a specific representative ("View as Representative"),
  // scope the household list to that rep, overriding the manual rep filter.
  // displayIsAdmin is the view-as-aware admin flag: employee households are
  // hidden whenever the DISPLAY role is non-admin (the backend can't hide them —
  // it still sees the real Admin token — so we filter client-side to match).
  const { displayRepId, displayIsAdmin } = useViewAs()
  const effectiveRepId = computed(() => displayRepId.value ?? repId.value)

  const householdQuery = computed(() => ({
    search: debounced.value.length >= 2 ? debounced.value : undefined,
    page: 1,
    pageSize: (term.value || filterActiveForFetch.value) ? 200 : undefined,
    minValue: minValueD.value ? Number(minValueD.value) : undefined,
    maxValue: maxValueD.value ? Number(maxValueD.value) : undefined,
    inceptionFrom: inceptionFromD.value || undefined,
    inceptionTo: inceptionToD.value || undefined,
    repId: effectiveRepId.value ?? undefined,
    sort: sortBy.value
  }))

  const { data: households, status: householdsStatus } = useFetch('/api/ui/households', {
    query: householdQuery,
    // Watch + fetch only for users who can actually see the selector.
    // For Clients, immediate: false skips the initial fetch and
    // `watch: false` keeps the typed-search refs from triggering a
    // later one (the selector UI is hidden behind v-if anyway, so
    // term / pageSizeHousehold won't actually mutate, but better to be
    // belt-and-suspenders so a future refactor can't reintroduce the 403).
    watch: canViewSelector ? [term, pageSizeHousehold, minValueD, maxValueD, inceptionFromD, inceptionToD, effectiveRepId, sortBy] : false,
    immediate: canViewSelector,
    credentials: 'include',
    default: () => ({ items: [] }),
    key: computed(() => `households:${term.value}:${pageSizeHousehold.value}:${minValueD.value}:${maxValueD.value}:${inceptionFromD.value}:${inceptionToD.value}:${effectiveRepId.value}:${sortBy.value}`)
  })

  const isHouseholdsLoading = computed(() => householdsStatus.value === 'pending')

  // True iff the picker has a debounce window open OR an active fetch
  // in flight. Drives the spinner on UCommandPalette so the loading
  // state clears the moment results are visible — previously a
  // `searchTerm.length > 0` guard left the spinner spinning forever
  // once the input had any text.
  const isSearching = computed(() =>
    debouncePending.value || householdsStatus.value === 'pending'
  )

  const groups = computed(() => {
    const raw = households.value?.items ?? []
    // Hide Orion category=="Employee" households when the DISPLAY role is a
    // non-admin preview ("View as Advisor/Client"). Real admins/advisors keep
    // seeing them (displayIsAdmin true), matching the backend's real-role filter.
    const visible = displayIsAdmin.value ? raw : raw.filter((h: any) => !h.isEmployee)
    const items = visible.map((h: any) => ({
      label: h.householdName,
      suffix: hashId(h.householdId),
      description: `${h.repName} • ${USD.format(h.currentValue)} `,
      householdId: h.householdId,
      repName: h.repName,
      value: h.currentValue,
      class: 'cursor-pointer'
    }))

    return [{
      id: 'households',
      // Server is already filtering by `search`, so the palette MUST
      // NOT re-filter locally — otherwise during the 250ms debounce
      // window between keystroke and next fetch, the palette would
      // try to match the new term against the previous fetch's
      // items and render "no results" until the user removed or
      // added a character (which happened to re-prefix-match the
      // stale cache). The bug was extremely repeatable when typing
      // fast: the cached items never had the new prefix mid-window.
      ignoreFilter: true,
      items
    }]
  })

  function onSelect(item: any) {
    if (!item) return
    tabSession.clearSession()
    tabSession.setHouseholdId(item.householdId)
    const householdObj: Household = {
      id: item.householdId,
      repName: item.repName,
      name: item.label,
      value: item.value
    }
    tabSession.setHouseholdObj(householdObj)
    void router.push('/household/overview')
  }

  /**
   * Re-hydrate the picker's selection from the persisted tab session
   * after a refresh. Call from the layout's onNuxtReady block.
   */
  function restoreFromSession() {
    if (tabSession.household) {
      selectedHousehold.value = {
        label: tabSession.household.name,
        suffix: hashId(tabSession.household.id),
        description: `${tabSession.household.repName} - ${USD.format(tabSession.household.value ?? 0)}`,
        householdId: tabSession.household.id,
        repName: tabSession.household.repName,
        value: tabSession.household.value,
        class: 'cursor-pointer'
      }
    }
  }

  // Keep THIS picker instance's displayed selection in sync with the shared
  // tab-session household. A selection made by a DIFFERENT instance — e.g. the
  // /advisor landing page builds its own useHouseholdPicker() — only writes to
  // the store via onSelect, so without this the header pill (a separate
  // instance) would stay stale. Watch the id (primitive) and re-hydrate the
  // label from the store; clear it when the household is deselected.
  watch(() => tabSession.householdId, () => {
    if (tabSession.household) restoreFromSession()
    else selectedHousehold.value = null
  })

  return {
    // state
    searchTerm,
    selectedHousehold,
    households,
    isHouseholdsLoading,
    householdsStatus,
    isSearching,
    groups,
    canViewSelector,
    // picker filters (AUM + inception range)
    minValue,
    maxValue,
    inceptionFrom,
    inceptionTo,
    pickerFilterActive,
    // sort
    sortBy,
    sortOptions,
    // advisor (representative) filter
    canFilterByRep,
    repSearch,
    repId,
    selectedRepName,
    repItems,
    repIsSearching,
    pickRep,
    clearRep,
    // actions
    onSelect,
    restoreFromSession,
    resetPickerFilters
  }
}
